import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageMeta } from '../../core/usePageMeta';
import { ROUTES } from '../../app/routes';
import { publicApi, type PostCard } from './publicApi';
import { EmptyState, ErrorState, LoadingGrid, PublicLayout, usePublicLanguage } from './components';
import { PostCardView, useLikedPosts, useOwnPosts } from './PostCard';
import { PostComposer } from './PostComposer';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';

/**
 * `/social` — every business's public posts, newest first, with the same forgiving search as the
 * directory ("durr" finds posts from Durrës). `?post=<id>` (a shared link) shows that post alone.
 */
export function SocialPage() {
  const { t } = useTranslation();
  usePageMeta(t('hub.posts.title', 'Social'));
  const lang = usePublicLanguage();
  const [params, setParams] = useSearchParams();
  const postId = params.get('post');
  const [posts, setPosts] = useState<PostCard[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [q, setQ] = useState(params.get('q') ?? '');
  const liked = useLikedPosts(posts);
  const own = useOwnPosts(posts);

  useEffect(() => {
    let active = true;
    setPosts(null);
    setError(false);
    publicApi.posts(lang, postId ? { id: postId } : { q: params.get('q') ?? undefined, city: params.get('city') ?? undefined, pageSize: 12 })
      .then((page) => { if (active) { setPosts(page.items); setCursor(page.nextCursor); } })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [lang, params, postId, attempt]);

  async function more() {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const page = await publicApi.posts(lang, { q: params.get('q') ?? undefined, city: params.get('city') ?? undefined, cursor, pageSize: 12 });
      setPosts((current) => {
        const known = new Set((current ?? []).map((post) => post.id));
        return [...(current ?? []), ...page.items.filter((post) => !known.has(post.id))];
      });
      setCursor(page.nextCursor);
    } catch { setError(true); }
    finally { setLoadingMore(false); }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const next = new URLSearchParams();
    if (q.trim()) next.set('q', q.trim());
    setParams(next);
  }

  return <PublicLayout>
    <div className="hub-wrap hub-section hub-social">
      <header className="hub-social-heading"><div>
        <span className="hub-social-eyebrow">{t('hub.posts.community', 'The local community')}</span>
        <h1 className="hub-page-title">{t('hub.posts.title', 'Social')}</h1>
        <p>{t('hub.posts.intro', 'A little closer to the businesses around you.')}</p>
      </div>
        <div className="hub-section-tools">
          {postId && <Link to={ROUTES.social}>{t('hub.posts.allPosts', 'All posts →')}</Link>}
        </div></header>
      <div className="hub-social-layout">
      <aside className="hub-social-sidebar">
        <div className="hub-social-discover">
          <span className="hub-social-symbol"><ForumOutlinedIcon aria-hidden /></span>
          <h2>{t('hub.posts.discoverTitle', 'Local stories. New discoveries.')}</h2>
          <p>{t('hub.posts.discoverText', 'News, photos and everyday moments, shared by businesses in your community.')}</p>
          <Link to={ROUTES.search}>{t('hub.posts.exploreBusinesses', 'Explore businesses')}<ArrowForwardRoundedIcon aria-hidden /></Link>
        </div>
      </aside>
      <div className="hub-social-main">
      {!postId && <form className="hub-search hub-social-search" role="search" onSubmit={submit}>
        <SearchRoundedIcon className="hub-social-search-icon" aria-hidden />
        <label className="hub-sr" htmlFor="hub-post-q">{t('hub.posts.searchLabel', 'Business, city or text')}</label>
        <input id="hub-post-q" value={q} onChange={(event) => setQ(event.target.value)} placeholder={t('hub.posts.searchLabel', 'Business, city or text')} />
        <button type="submit">{t('hub.search.submit', 'Search')}</button>
      </form>}
      {!postId && <div className="hub-social-feed-label"><h2>{params.get('q') || params.get('city') ? t('hub.posts.searchResults', 'Search results') : t('hub.posts.latest', 'Latest updates')}</h2><span>{t('hub.posts.newestFirst', 'Newest first')}</span></div>}
      {!postId && <PostComposer onPublished={() => setAttempt((value) => value + 1)} />}
      {error && <ErrorState message={t('hub.posts.loadError', 'Posts could not be loaded.')} onRetry={() => setAttempt((value) => value + 1)} />}
      {!posts && !error && <LoadingGrid count={3} label={t('hub.loading', 'Loading')} />}
      {posts && posts.length === 0 && <EmptyState>{postId ? t('hub.posts.notFound', 'This post is no longer available.') : t('hub.posts.empty', 'No posts yet. Businesses share news and photos here.')}</EmptyState>}
      {posts && posts.length > 0 && <div className="hub-posts feed">{posts.map((post) => <PostCardView key={post.id} post={post} liked={liked.has(post.id)} own={own.has(post.id)}
        onDeleted={(id) => setPosts((current) => current?.filter((item) => item.id !== id) ?? null)} />)}</div>}
      {!postId && cursor && <div className="hub-pager">
        <button type="button" className="hub-btn sm ghost" onClick={more} disabled={loadingMore}>
          {loadingMore ? `${t('hub.loading', 'Loading')}…` : t('hub.posts.loadMore', 'Show more')}
        </button>
      </div>}
      </div>
      </div>
    </div>
  </PublicLayout>;
}
